import React, { useState, createContext, useContext } from "react";
import { useDispatch } from "react-redux";

import {
  AppDispatch,
  ProductRole,
  UserProductPermission,
  Role,
  UserPermission,
  Access,
} from "types";
import { getPermissions } from "./actions";
import {
  ROLENAMES,
  ACCESSNAMES,
  FULL_ACCESS_KEY,
  READ_ACCESS_KEY,
} from "./permissions";

interface PermissionsState {
  permissions: UserProductPermission[];
  menuPermissions: string[];
  hasReadPermission: (productId: number, role: string) => boolean;
  hasFullPermission: (productId: number, roleKey: string) => boolean;
  hasAtLeastOneFullPermission: (roleKey: string) => boolean;
  hasPermission: (productId: number, roleKey: string, accessKey: string) => boolean;
}

export interface PermissionContextProps {
  permissionContext: {
    permissions: PermissionsState;
    user: string[];
    refresh: ((userName?: string) => Promise<void>)[];
  }
}

const initialContextState: PermissionContextProps = {
  permissionContext: {
    permissions: {
      permissions: [],
      menuPermissions: [],
      hasReadPermission: () => false,
      hasFullPermission: () => false,
      hasAtLeastOneFullPermission: () => false,
      hasPermission: () => false,
    },
    user: [""],
    refresh: [async () => {}],
  },
};

export const PermissionContext = createContext<PermissionContextProps>(initialContextState);

export const PermissionProvider = ({
  children,
}: JSX.ElementChildrenAttribute): JSX.Element => {
  const dispatch: AppDispatch = useDispatch();
  const [user, setUser] = useState("");
  const [permissions, setPermissions] = useState<UserProductPermission[]>([]);
  const [menuPermissions, setMenuPermissions] = useState<string[]>([]);

  const getMenuPermissions = (productPermissions: UserProductPermission[]) => {
    let newMenuPermissions: string[] = [];
    if (productPermissions && productPermissions.length > 0) {
      productPermissions.map((productPermission) => {
        if (productPermission && productPermission.permissions) {
          productPermission.permissions.map((permission) => {
            for (const [key, roleName] of Object.entries(ROLENAMES)) {
              if (
                roleName.id === permission.roleId &&
                !newMenuPermissions.includes(roleName.oldName)
              ) {
                newMenuPermissions = [...newMenuPermissions, roleName.oldName];
              }
            }
          });
        }
      });
    }
    setMenuPermissions(newMenuPermissions);
  };

  const addAccessToPermissionsList = (accesses: Access[], role: Role, userProductPermission: UserProductPermission) => {
    if (accesses && accesses.length > 0) {
      accesses.map((subAccess: Access) => {
        const newSubPermission: UserPermission = {
          roleId: role.roleId,
          accessId: subAccess.accessId,
          productRoleAccessId: subAccess.productRoleAccessId,
        };
        userProductPermission.permissions = [
          ...userProductPermission.permissions,
          newSubPermission,
        ];

        // add subAccesses recursively
        if (Array.isArray(subAccess.subAccesses)) {
          addAccessToPermissionsList(subAccess.subAccesses, role, userProductPermission);
        }
      });
    }
  }

  const processPermissions = (productRoles: ProductRole[]) => {
    let newPermissions: UserProductPermission[] = [];
    if (productRoles) {

      // per product
      productRoles.map((productRole: ProductRole) => {
        const newUserProductPermission: UserProductPermission = {
          productId: productRole.productId,
          productName: productRole.productName,
          permissions: [],
        };
        if (productRole.roles && productRole.roles.length > 0) {
          productRole.roles.map((role: Role) => {
            if (Array.isArray(role.accesses)) {
              addAccessToPermissionsList(role.accesses, role, newUserProductPermission);
            }
          });
        }
        newPermissions = [...newPermissions, newUserProductPermission];
      });
    }
    setPermissions(newPermissions);
    getMenuPermissions(newPermissions);
  };

  const refreshPermissions = (userName?: string) => {
    return dispatch(getPermissions()).then((productRoles: ProductRole[]) => {
      processPermissions(productRoles);
      if (!user && userName) {
        setUser(userName);
      }
    });
  };

  const hasReadPermission = (productId: number, role: string) => {
    return permissions.some(
      (productPermission) =>
        productPermission.productId === productId &&
        productPermission.permissions.some(
          (permission) =>
            permission.roleId === ROLENAMES[role].id &&
            permission.accessId === ACCESSNAMES[READ_ACCESS_KEY].id
        )
    );
  };

  const hasFullPermission = (productId: number, roleKey: string) => {
    return permissions.some(
      (productPermission) =>
        productPermission.productId === productId &&
        productPermission.permissions.some(
          (permission) =>
            permission.roleId === ROLENAMES[roleKey].id &&
            permission.accessId === ACCESSNAMES[FULL_ACCESS_KEY].id
        )
    );
  };

  const hasAtLeastOneFullPermission = (roleKey: string) => {
    return permissions.some((productPermission) =>
      productPermission.permissions.some(
        (permission) =>
          permission.roleId === ROLENAMES[roleKey].id &&
          permission.accessId === ACCESSNAMES[FULL_ACCESS_KEY].id
      )
    );
  };

  const hasPermission = (
    productId: number,
    roleKey: string,
    accessKey: string
  ) => {
    return permissions.some(
      (productPermission) =>
        productPermission.productId === productId &&
        productPermission.permissions.some(
          (permission) =>
            permission.roleId === ROLENAMES[roleKey].id &&
            permission.accessId === ACCESSNAMES[accessKey].id
        )
    );
  };

  return (
    <PermissionContext.Provider
      value={{
        permissionContext: {
          permissions: {
            permissions,
            menuPermissions,
            hasReadPermission,
            hasFullPermission,
            hasAtLeastOneFullPermission,
            hasPermission,
          },
          user: [user],
          refresh: [refreshPermissions],
        },
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

const usePermission = () => useContext(PermissionContext);
export default usePermission;
